// src/components/compliance/modals/RiskGuidanceModal.jsx

import React from "react";
import { MetaField } from "../../remediation/SharedRemediationUI";
import {
  safeNumber,
  normalizeText,
  cleanValue,
  buildControlLabel,
  getTaskStatusLabel,
  getDisplayStateLabel,
  getSummaryChipStyle,
} from "../../../utils/complianceFormatters";
import { buildFixPlanSteps } from "../../../utils/remediationFixPlans";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

function getRelatedSopTitle(sop) {
  return (
    sop?.title ||
    sop?.sop_title ||
    sop?.name ||
    sop?.code ||
    "Untitled SOP"
  );
}

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 16,
};

const itemCardStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 12,
};

function buildReturnOptions(returnTarget) {
  if (returnTarget === "risk_queue") return { returnTo: "risk_queue" };
  if (returnTarget === "all_active_tasks") return { returnTo: "all_active_tasks" };
  if (returnTarget === "queue_status_tasks") return { returnTo: "queue_status_tasks" };
  if (returnTarget === "active_findings") return { returnTo: "active_findings" };
  if (returnTarget === "approved_exceptions") return { returnTo: "approved_exceptions" };
  if (returnTarget === "validation_failed") return { returnTo: "validation_failed" };
  if (returnTarget === "overdue_tasks") return { returnTo: "overdue_tasks" };
  if (returnTarget === "in_progress_tasks") return { returnTo: "in_progress_tasks" };
  if (returnTarget === "closed_tasks") return { returnTo: "closed_tasks" };
  if (returnTarget === "posture_status") return { returnTo: "posture_status" };
  if (returnTarget === "stale_controls") return { returnTo: "stale_controls" };
  return {};
}

function getBackLabel(returnTarget) {
  if (returnTarget === "risk_queue") return "Back to Queue";
  if (returnTarget === "all_active_tasks") return "Back to All Active";
  if (returnTarget === "queue_status_tasks") return "Back to Queue";
  if (returnTarget === "active_findings") return "Back to Findings";
  if (returnTarget === "approved_exceptions") return "Back to Approved Exceptions";
  if (returnTarget === "validation_failed") return "Back to Validation Failed";
  if (returnTarget === "overdue_tasks") return "Back to Overdue";
  if (returnTarget === "in_progress_tasks") return "Back to In Progress";
  if (returnTarget === "closed_tasks") return "Back to Closed";
  if (returnTarget === "posture_status") return "Back to Posture Status";
  if (returnTarget === "stale_controls") return "Back to Stale Controls";
  return "Close";
}

export default function RiskGuidanceModal({
  item,
  framework,
  relatedSops = [],
  recommendedActions = [],
  linkedTask = null,
  returnTarget = null,
  onClose,
  onViewTask,
  onOpenFixPlan,
  onOpenDrift,
}) {
  if (!item) return null;

  const guidanceItem = item || {};
  const guidanceControlId = guidanceItem.control_id || guidanceItem.controlId || "";
  const guidanceRuleId = guidanceItem.rule_id || guidanceItem.ruleId || "";
  const guidanceResourceId = guidanceItem.resource_id || guidanceItem.resourceId || "";
  const guidanceState = guidanceItem.state || guidanceItem.status || guidanceItem.control_state || "non-passing";
  const guidanceReason =
    guidanceItem.reason_summary ||
    `${buildControlLabel(guidanceControlId, guidanceRuleId)} is currently ${getDisplayStateLabel(guidanceState)}.`;
  const guidanceSteps = buildFixPlanSteps(guidanceItem, relatedSops, linkedTask || {});
  const guidancePrimarySop = relatedSops?.[0] || null;
  const guidanceRecommendedAction =
    recommendedActions?.[0] ||
    guidanceSteps?.[0] ||
    "Open the Fix Plan to review the recommended remediation steps.";
  const returnOptions = buildReturnOptions(returnTarget);
  const stateText = getDisplayStateLabel(guidanceState, "—");
  const stateColor = normalizeText(guidanceState).includes("fail") ? "#f87171" : "#60a5fa";

  return (
    <ModalShell
      open
      ariaLabel="Risk guidance"
      eyebrow="OASYS · RISK GUIDANCE"
      title={buildControlLabel(guidanceControlId, guidanceRuleId)}
      subtitle="Read-only context for this risk. No task, draft, or source-system change is created from Guidance."
      onClose={onClose}
      maxWidth="940px"
      accentBorder="rgba(74, 222, 128, 0.45)"
      zIndex={90}
    >
      <div style={metricGridStyle}>
        <MetricTile
          label="Framework"
          value={cleanValue(guidanceItem.framework, framework)}
          sub="selected posture scope"
          color="#60a5fa"
        />
        <MetricTile label="State" value={stateText} sub="current control state" color={stateColor} />
        <MetricTile
          label="Open Findings"
          value={guidanceItem.open_findings_total === "—" ? "—" : safeNumber(guidanceItem.open_findings_total, 0)}
          sub="active evidence burden"
          color={safeNumber(guidanceItem.open_findings_total, 0) > 0 ? "#fbbf24" : "#34d399"}
        />
      </div>

      <ModalSection
        title="Guidance Activity"
        description="Review the audit context, recommended remediation direction, SOP linkage, and task state."
        badge={linkedTask ? "task linked" : "no task"}
        badgeTone={linkedTask ? "ok" : "warn"}
      >
        <article style={itemCardStyle}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ ...getSummaryChipStyle("warn"), fontWeight: 900 }}>{stateText}</span>
            <span style={getSummaryChipStyle(guidancePrimarySop ? "ok" : "default")}>
              {guidancePrimarySop ? "SOP Linked" : "No Linked SOP"}
            </span>
          </div>

          <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 18, lineHeight: 1.25 }}>
            What this risk means
          </h4>
          <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {guidanceReason}
          </p>
          {guidanceItem.remediation_hint ? (
            <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: "#f8fafc" }}>Recommended direction:</strong> {guidanceItem.remediation_hint}
            </p>
          ) : null}
        </article>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
          <article style={itemCardStyle}>
            <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 16, lineHeight: 1.25 }}>
              Recommended Next Action
            </h4>
            <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              {guidanceRecommendedAction}
            </p>
          </article>

          <article style={itemCardStyle}>
            <h4 style={{ margin: 0, color: "#f8fafc", fontSize: 16, lineHeight: 1.25 }}>
              SOP and Task Status
            </h4>
            <MetaField
              label="Linked SOP"
              value={guidancePrimarySop ? getRelatedSopTitle(guidancePrimarySop) : "No linked SOP"}
              wrap
            />
            <MetaField
              label="Task"
              value={linkedTask ? getTaskStatusLabel(linkedTask.status) : "No tracked task"}
              wrap
            />
          </article>
        </div>

        <article style={itemCardStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
            <MetaField label="Control" value={cleanValue(guidanceControlId)} />
            <MetaField label="Rule" value={cleanValue(guidanceRuleId)} wrap />
            <MetaField label="Resource" value={cleanValue(guidanceResourceId)} wrap />
          </div>

          <ModalActionRow
            actions={[
              linkedTask
                ? {
                    key: "review",
                    label: "Review / Manage",
                    onClick: () => onViewTask(linkedTask, returnOptions),
                  }
                : null,
              {
                key: "fix-plan",
                label: "Fix Plan",
                onClick: () => onOpenFixPlan(guidanceItem, "risk", returnOptions),
              },
              {
                key: "drift",
                label: "Open in Drift",
                onClick: () =>
                  onOpenDrift({
                    framework: guidanceItem.framework || framework,
                    controlId: guidanceControlId || undefined,
                    ruleId: guidanceRuleId || undefined,
                    resourceId: guidanceResourceId || undefined,
                  }),
              },
              {
                key: "close",
                label: getBackLabel(returnTarget),
                onClick: onClose,
              },
            ]}
          />
        </article>
      </ModalSection>
    </ModalShell>
  );
}
